import { DecimalPipe, formatNumber } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    InputSignal,
    LOCALE_ID,
    OutputEmitterRef,
    Signal,
    WritableSignal,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { BlockDirective, ElemDirective, ModDirective } from '@rt-tools/core';
import { NotificationBus, RtButtonDirective } from '@rt-tools/ui-kit-v2';
import { FeedbackFormComponent } from '@stair-craft/common/components';
import {
    DEFAULT_STAIR_CONFIG,
    IStairCalculationResult,
    IStairConfig,
    IValidationItem,
    IValidationSeverity,
    SHOW_MATERIAL_COST,
} from '@stair-craft/common/util';

import { readStairConfigFile, stairConfigLoadErrorMessage } from './stair-config-file.loader';

const BEM_BLOCK: string = 'sc-results-panel';

/** One row of the norms check: the norm, its text mark and, for headroom, the measured value. */
interface INormRow {
    code: string;
    label: string;
    severity: IValidationSeverity;
    mark: string;
    value: string | null;
}

/** The validation codes the norms rows show, in the order of the mockup. */
const NORM_ROW_CODES: readonly string[] = ['BLONDEL', 'ANGLE', 'STAIR_WIDTH', 'HEADROOM'];

/** The row whose tag carries the measured value instead of the mark. */
const VALUE_TAG_CODE: string = 'HEADROOM';

function normLabel(code: string): string {
    switch (code) {
        case 'BLONDEL':
            return $localize`:@@resultBlondel:Формула Блонделя`;
        case 'ANGLE':
            return $localize`:@@resultAngle:Угол наклона`;
        case 'STAIR_WIDTH':
            return $localize`:@@labelStairWidth:Ширина марша`;
        default:
            return $localize`:@@normHeadroom:Просвет над ступенью`;
    }
}

function normMark(severity: IValidationSeverity): string {
    switch (severity) {
        case 'error':
            return $localize`:@@normError:ошибка`;
        case 'warning':
            return $localize`:@@normWarning:на границе`;
        default:
            return $localize`:@@normOk:в норме`;
    }
}

@Component({
    selector: 'sc-results-panel',
    templateUrl: './results-panel.component.html',
    styleUrl: './results-panel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // angular
        DecimalPipe,
        RouterLink,

        // rt-tools
        BlockDirective,
        ElemDirective,
        ModDirective,
        RtButtonDirective,
        FeedbackFormComponent,
    ],
    host: {
        class: BEM_BLOCK,
    },
})
export class ResultsPanelComponent {
    readonly #notifications: NotificationBus = inject(NotificationBus);
    readonly #locale: string = inject(LOCALE_ID);

    protected readonly dropActive: WritableSignal<boolean> = signal<boolean>(false);
    protected readonly showCost: boolean = SHOW_MATERIAL_COST;

    protected readonly normRows: Signal<INormRow[]> = computed((): INormRow[] => {
        const validation: IValidationItem[] = this.result()?.validation ?? [];
        return NORM_ROW_CODES.flatMap((code: string): INormRow[] => {
            const item: IValidationItem | undefined = validation.find(
                (validationItem: IValidationItem): boolean => validationItem.code === code
            );
            if (!item) {
                return [];
            }
            const unit: string = $localize`:@@unitMm:мм`;
            const value: string | null =
                code === VALUE_TAG_CODE && item.value !== null ? `${formatNumber(item.value, this.#locale, '1.0-0')} ${unit}` : null;
            return [{ code, value, label: normLabel(code), severity: item.severity, mark: normMark(item.severity) }];
        });
    });

    /** The first failed check, or the first borderline one when nothing failed. */
    protected readonly normNotice: Signal<IValidationItem | null> = computed((): IValidationItem | null => {
        const validation: IValidationItem[] = this.result()?.validation ?? [];
        return (
            validation.find((validationItem: IValidationItem): boolean => validationItem.severity === 'error') ??
            validation.find((validationItem: IValidationItem): boolean => validationItem.severity === 'warning') ??
            null
        );
    });

    public readonly config: InputSignal<IStairConfig> = input<IStairConfig>(DEFAULT_STAIR_CONFIG);
    public readonly result: InputSignal<IStairCalculationResult | null> = input<IStairCalculationResult | null>(null);
    public readonly justCopied: InputSignal<boolean> = input<boolean>(false);

    public readonly configChange: OutputEmitterRef<IStairConfig> = output<IStairConfig>();
    public readonly exportRequested: OutputEmitterRef<void> = output();
    public readonly dxfRequested: OutputEmitterRef<void> = output();
    public readonly partsRequested: OutputEmitterRef<void> = output();
    public readonly shareRequested: OutputEmitterRef<void> = output();
    public readonly feedbackOpened: OutputEmitterRef<void> = output();
    public readonly feedbackSubmitted: OutputEmitterRef<void> = output();
    public readonly feedbackFailed: OutputEmitterRef<{ reason: string }> = output<{ reason: string }>();

    protected onFileSelected(event: Event): void {
        const picker: HTMLInputElement = event.target as HTMLInputElement;
        const file: File | undefined = picker.files?.[0];
        if (file) {
            this.#loadFile(file);
        }
        picker.value = '';
    }

    protected onDrop(event: DragEvent): void {
        event.preventDefault();
        this.dropActive.set(false);
        const file: File | undefined = event.dataTransfer?.files[0];
        // A name that is not `.json` is refused by the loader with a message, not in silence.
        if (file) {
            this.#loadFile(file);
        }
    }

    protected onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.dropActive.set(true);
    }

    protected onDragLeave(): void {
        this.dropActive.set(false);
    }

    #loadFile(file: File): void {
        readStairConfigFile(file).then(
            (loaded: IStairConfig): void => {
                this.configChange.emit(loaded);
            },
            (error: unknown): void => {
                this.#notifications.error(stairConfigLoadErrorMessage(error));
            }
        );
    }
}
